import { useMemo, useCallback, useRef, useEffect, useState, type Ref } from "react"
import OpLayoutEngineRenderer from "connected-components/op-layout-engine/renderer"
import { TEMPLATES } from "connected-components/op-layout-engine/templates"
import type { OpLayoutEngineRendererActions } from "connected-components/op-layout-engine/types"
import type { InstrumentWithMetadata } from "helpers/types/instruments/metadata"
import { useAppDispatch, useAppSelector } from "hooks/redux"
import { useDashboardId } from "../context/dashboard-id"
import { useRemoveModule } from "../hooks/use-remove-module"
import { useAddModule } from "../hooks/use-add-module"
import { useUpdateModule } from "../hooks/use-update-module"
import { panelsSlice, isDefaultModulesSnapshot, EMPTY_MODULES } from "../store/panels-slice"
import { useHasThematicBetasContentSet } from "../hooks/use-has-thematic-betas-content-set"
import { DEFAULT_FACTORS_THEMATIC_CONTENT_SET_ID, DEFAULT_THEMATIC_SORT_METRIC_ID } from "../modules/constants"
import {
  DashboardOpLayoutModuleDependenciesContext,
  type DashboardOpLayoutModuleDependencies,
} from "../context/dashboard-op-layout-module-dependencies"
import { useDashboardOpLayoutEnginePersistence } from "../hooks/use-op-layout-engine-persistence"
import { useDashboardModuleActions } from "../hooks/use-dashboard-module-actions"
import { createModuleComponents } from "../helpers/module-renderers"
import {
  registerDashboardLayoutPanelState,
  unregisterDashboardLayoutPanelState,
} from "../helpers/dashboard-layout-panel-state"
import { dashboardEvents, DASHBOARD_EVENTS } from "helpers/events/dashboard-events"
import { MODULE_TYPES } from "../modules/types"

interface DashboardModulesRendererProps {
  instrumentsWithMetadata: Record<string, InstrumentWithMetadata>
  hasMultipleInstruments?: boolean
  contentSets?: unknown[]
  modelMetadata?: unknown
  dockviewLayoutRef?: Ref<OpLayoutEngineRendererActions | null>
  onLayoutReady?: (actions: OpLayoutEngineRendererActions | null) => void
}

/**
 * Renders dashboard modules using OpLayoutEngine
 */
export default function DashboardModulesRenderer({
  instrumentsWithMetadata,
  hasMultipleInstruments,
  contentSets,
  modelMetadata,
  dockviewLayoutRef,
  onLayoutReady,
}: DashboardModulesRendererProps) {
  const dispatch = useAppDispatch()
  const dashboardId = useDashboardId()
  const panels = useAppSelector((s) => s.dashboard.panels[dashboardId])
  const modules = panels?.modules ?? EMPTY_MODULES
  const isModulesPreferencesLoaded = panels?.isPreferencesLoaded ?? false
  const removeModule = useRemoveModule()
  const updateModule = useUpdateModule()

  const { hasThematicBetasContentSet } = useHasThematicBetasContentSet()

  const addModule = useAddModule()

  // Auto-inject the thematic-betas panel when an entitled account's layout is
  // exactly the unentitled five-default snapshot — covers users who gained
  // entitlement after their dashboard was first seeded. The `.some()` + snapshot
  // checks block re-injection while the panel is present; a manual removal by
  // the user then matches the snapshot again, so the effect would re-add it.
  // That's the intended pre-RTK behaviour; we preserve it rather than latch.
  useEffect(() => {
    if (!isModulesPreferencesLoaded || !hasThematicBetasContentSet) return
    if (modules.some((m) => m.config?.initialView === DEFAULT_FACTORS_THEMATIC_CONTENT_SET_ID)) return
    if (!isDefaultModulesSnapshot(modules)) return
    addModule({
      type: MODULE_TYPES.FACTORS,
      config: {
        initialView: DEFAULT_FACTORS_THEMATIC_CONTENT_SET_ID,
        sortMetricId: DEFAULT_THEMATIC_SORT_METRIC_ID,
        title: "Thematic Betas",
      },
    })
  }, [isModulesPreferencesLoaded, hasThematicBetasContentSet, modules, addModule])

  const opLayoutEnginePersistence = useDashboardOpLayoutEnginePersistence(modules, isModulesPreferencesLoaded)
  const { handleRemoveModule, handleUpdateModuleTitle, handleDuplicateModule } = useDashboardModuleActions(modules, {
    removeModule,
    updateModule,
    addModule,
  })

  const layoutEngineActionsRef = useRef<OpLayoutEngineRendererActions | null>(null)

  useEffect(() => {
    return dashboardEvents.on<{ panelId: string }>(
      DASHBOARD_EVENTS.DRILLDOWN_FOCUS_PANEL,
      ({ panelId }) => layoutEngineActionsRef.current?.focusPanel(panelId),
    )
  }, [])

  useEffect(() => {
    registerDashboardLayoutPanelState({
      isPanelMounted: (panelId) => layoutEngineActionsRef.current?.isPanelMounted(panelId) ?? false,
      ensurePanel: (panelId) => layoutEngineActionsRef.current?.ensurePanel(panelId) ?? false,
    })
    return unregisterDashboardLayoutPanelState
  }, [])

  const pendingResetApiRef = useRef<Parameters<OpLayoutEngineRendererActions["resetLayout"]>[0] | null>(null)
  const didRequestResetRef = useRef(false)
  const [layoutActionsVersion, setLayoutActionsVersion] = useState(0)

  useEffect(() => {
    if (!didRequestResetRef.current || !pendingResetApiRef.current) {
      return
    }
    if (!isDefaultModulesSnapshot(modules, hasThematicBetasContentSet)) {
      return
    }

    const runEngineReset = layoutEngineActionsRef.current?.resetLayout
    if (!runEngineReset) {
      return
    }

    const api = pendingResetApiRef.current
    runEngineReset(api)
    pendingResetApiRef.current = null
    didRequestResetRef.current = false
  }, [modules, hasThematicBetasContentSet, layoutActionsVersion])

  const wrappedOnLayoutReady = useCallback(
    (actions: OpLayoutEngineRendererActions | null) => {
      layoutEngineActionsRef.current = actions
      if (actions && didRequestResetRef.current) {
        setLayoutActionsVersion((version: number) => version + 1)
      }

      if (!onLayoutReady) {
        return
      }
      if (!actions) {
        onLayoutReady(null)
        return
      }
      onLayoutReady({
        ...actions,
        resetLayout: (api) => {
          didRequestResetRef.current = true
          pendingResetApiRef.current = api
          dispatch(panelsSlice.actions.resetModulesToDefault({ dashboardId, hasThematicBetasContentSet }))
        },
      })
    },
    [onLayoutReady, dispatch, dashboardId, hasThematicBetasContentSet],
  )

  const components = useMemo(() => {
    return createModuleComponents(modules, {
      instrumentsWithMetadata,
      hasMultipleInstruments,
      contentSets,
      modelMetadata,
      updateModule,
      onRemoveModule: handleRemoveModule,
      onUpdateModuleTitle: handleUpdateModuleTitle,
      onDuplicateModule: handleDuplicateModule,
    })
  }, [modules, contentSets, hasMultipleInstruments, instrumentsWithMetadata, modelMetadata, updateModule, handleRemoveModule, handleUpdateModuleTitle, handleDuplicateModule])

  const dashboardOpLayoutModuleDependencies = useMemo((): DashboardOpLayoutModuleDependencies => ({
    instrumentsWithMetadata,
    hasMultipleInstruments,
    contentSets,
    modelMetadata,
  }), [instrumentsWithMetadata, hasMultipleInstruments, contentSets, modelMetadata])

  return (
    <DashboardOpLayoutModuleDependenciesContext.Provider value={dashboardOpLayoutModuleDependencies}>
      <OpLayoutEngineRenderer
        layoutRef={dockviewLayoutRef ?? null}
        onLayoutReady={wrappedOnLayoutReady}
        components={components}
        template={TEMPLATES.HIERARCHICAL}
        persistence={opLayoutEnginePersistence}
        className="dashboard-op-layout"
      />
    </DashboardOpLayoutModuleDependenciesContext.Provider>
  )
}
